import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Ticket, Heart, Settings, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

/**
 * SHARED MOBILE LAYOUT COMPONENTS
 * These components provide consistent mobile UI across all user-facing pages.
 * Use MobileLayout as the wrapper for all mobile user pages.
 */

// Mobile Header - Logo + "Live" text + Auth buttons
export const MobileHeader = () => {
  const { user, isAuthenticated } = useAuth();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border" data-testid="mobile-header">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Logo + Live text */}
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/exit88-logo.png" 
            alt="EXIT88" 
            className="h-8 w-auto object-contain"
          />
          <span className="text-primary font-bold text-lg">LIVE</span>
        </Link>

        {/* Auth buttons */}
        <div className="flex items-center gap-2">
          {isAuthenticated && user ? (
            <Link to="/me/settings">
              <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-primary flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-xs">{user.name?.charAt(0) || 'U'}</span>
                  </div>
                )}
              </div>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground h-8 px-3 text-xs font-medium">
                  LOG IN
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3 text-xs font-medium">
                  SIGN UP
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

// Mobile Bottom Navigation
export const MobileBottomNav = () => {
  const location = useLocation();
  const path = location.pathname;
  
  const navItems = [
    { to: '/', icon: Home, label: 'Home', isActive: path === '/' || path === '/events' },
    { to: '/me/tickets', icon: Ticket, label: 'Tickets', isActive: path.startsWith('/me/tickets') },
    { to: '/me/wishlist', icon: Heart, label: 'Wishlist', isActive: path === '/me/wishlist' },
    { to: '/me/settings', icon: Settings, label: 'Settings', isActive: path === '/me/settings' },
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around h-16">
        {navItems.map(({ to, icon: Icon, label, isActive }) => (
          <Link
            key={to}
            to={to}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full transition-colors",
              isActive ? "text-primary" : "text-muted-foreground"
            )}
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs mt-1">{label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

// Desktop Header with full navigation - UNDERLINE nav styling (not boxed/pill)
export const DesktopHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const path = location.pathname;
  const [showDropdown, setShowDropdown] = React.useState(false);

  // Navigation item with UNDERLINE active/hover state (per design reference)
  const NavItem = ({ to, icon: Icon, label, active }) => (
    <Link
      to={to}
      className={cn(
        "relative flex items-center gap-2 px-4 py-5 transition-colors",
        active ? "text-primary" : "text-muted-foreground hover:text-white"
      )}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
      {/* Underline indicator for active/hover state */}
      <span className={cn(
        "absolute bottom-0 left-4 right-4 h-0.5 rounded-full transition-all",
        active ? "bg-primary" : "bg-transparent group-hover:bg-primary/50"
      )} />
    </Link>
  );

  return (
    <header className="hidden lg:block fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border" data-testid="desktop-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Live text */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/exit88-logo.png" 
              alt="EXIT88" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-primary font-bold text-xl">LIVE</span>
          </Link>

          {/* Desktop Navigation - with underline active/hover states */}
          <nav className="flex items-center gap-2">
            <NavItem to="/" icon={Home} label="Home" active={path === '/' || path === '/events'} />
            <NavItem to="/me/tickets" icon={Ticket} label="Tickets" active={path.startsWith('/me/tickets')} />
            <NavItem to="/me/wishlist" icon={Heart} label="Wishlist" active={path === '/me/wishlist'} />
            <NavItem to="/me/settings" icon={Settings} label="Settings" active={path === '/me/settings'} />
          </nav>

          {/* Auth */}
          <div className="flex items-center gap-3">
            {isAuthenticated && user ? (
              <div className="relative">
                <button 
                  className="flex items-center gap-2 hover:bg-secondary rounded-lg p-2 transition-colors"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-primary flex items-center justify-center">
                        <span className="text-primary-foreground font-bold text-sm">{user.name?.charAt(0) || 'U'}</span>
                      </div>
                    )}
                  </div>
                  <span className="text-white font-medium">{user.name}</span>
                </button>
                {showDropdown && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowDropdown(false)} />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
                      <div className="p-3 border-b border-border">
                        <p className="font-medium text-white">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <button 
                        className="w-full px-4 py-2 text-left text-destructive hover:bg-destructive/10"
                        onClick={() => { logout(); setShowDropdown(false); navigate('/'); }}
                      >
                        Log Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    LOG IN
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                    SIGN UP
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// WhatsApp Banner - MUST match Noire Élan banner in height/padding/typography
export const WhatsAppBanner = () => (
  <Card className="bg-gradient-to-r from-green-900/40 to-green-800/20 border-green-600/30 p-4 overflow-hidden" data-testid="whatsapp-banner">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 bg-green-600/30 rounded-xl flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white text-sm">Cart Abandonment</h3>
        <p className="text-xs text-green-300/80">Recover lost sales on WhatsApp</p>
      </div>
      <Link to="#" className="text-yellow-400 hover:text-yellow-300 font-semibold text-sm flex items-center gap-1 flex-shrink-0">
        Get demo
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  </Card>
);

// Noire Banner - Always at bottom of page content on mobile
export const NoireBanner = () => (
  <Card className="bg-gradient-to-r from-purple-900/40 to-purple-800/20 border-purple-600/30 p-4 overflow-hidden" data-testid="noire-banner">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
        <img src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100" alt="Noire Élan" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white text-sm">Noire Élan</h3>
        <p className="text-xs text-purple-300/80">Exclusive event collection</p>
      </div>
      <Link to="/events" className="text-primary hover:text-primary/80 font-semibold text-sm flex items-center gap-1 flex-shrink-0">
        Shop now
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  </Card>
);

// Mobile Content Wrapper - Manages padding for header and bottom nav
export const MobileContentWrapper = ({ children, className }) => (
  <div 
    className={cn(
      "lg:hidden min-h-screen bg-background",
      "pt-14", // Padding for fixed header (h-14)
      "pb-24", // Padding for bottom nav (h-16) + build marker + safe area
      className
    )}
    data-testid="mobile-content-wrapper"
  >
    {children}
  </div>
);

// Desktop Content Wrapper
export const DesktopContentWrapper = ({ children, className }) => (
  <div 
    className={cn(
      "hidden lg:block min-h-screen bg-background",
      "pt-20", // Padding for fixed header (h-16 + extra)
      "pb-12",
      className
    )}
    data-testid="desktop-content-wrapper"
  >
    {children}
  </div>
);

// Complete Mobile Layout - Use this to wrap mobile pages
export const MobileLayout = ({ children, showBottomNav = true }) => (
  <>
    <MobileHeader />
    <MobileContentWrapper>
      {children}
    </MobileContentWrapper>
    {showBottomNav && <MobileBottomNav />}
  </>
);

// Complete Desktop Layout - Use this to wrap desktop pages  
export const DesktopLayout = ({ children }) => (
  <>
    <DesktopHeader />
    <DesktopContentWrapper>
      {children}
    </DesktopContentWrapper>
  </>
);

// Responsive Layout - Automatically switches between mobile and desktop
export const ResponsiveUserLayout = ({ children, showBottomNav = true }) => (
  <div data-testid="responsive-layout">
    {/* Mobile Layout */}
    <div className="lg:hidden">
      <MobileHeader />
      <MobileContentWrapper>
        {children}
      </MobileContentWrapper>
      {showBottomNav && <MobileBottomNav />}
    </div>
    
    {/* Desktop Layout */}
    <div className="hidden lg:block">
      <DesktopHeader />
      <DesktopContentWrapper>
        {children}
      </DesktopContentWrapper>
    </div>
  </div>
);

export default {
  MobileHeader,
  MobileBottomNav,
  DesktopHeader,
  WhatsAppBanner,
  NoireBanner,
  MobileContentWrapper,
  DesktopContentWrapper,
  MobileLayout,
  DesktopLayout,
  ResponsiveUserLayout,
};
